import { ChangeDetectionStrategy, Component, forwardRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import moment, { duration } from 'moment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DateRange, DefaultMatCalendarRangeStrategy, MAT_DATE_RANGE_SELECTION_STRATEGY, MatCalendar } from '@angular/material/datepicker';
import { Store } from '@ngxs/store';
import { ControlValueAccessorConnector } from '@nimbus-library/features/shared/components/control-value-accessor-connector';
import {
  DateRangeQuickAction,
  DateTimeRangeValue,
} from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/filter-datetime-range.model';
import { dateBetweenValidator } from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/datetime-validator';
import { InputDatePickerHeaderComponent } from '@nimbus-library/features/shared/components/material/input-date-picker/input-date-picker-header/input-date-picker-header.component';
import { LocalizationState } from '@nimbus-library/features/localization/store/localization.state';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-filter-datetime-range-input',
  templateUrl: './nimbus-filter-datetime-range-input.component.html',
  styleUrls: ['./nimbus-filter-datetime-range-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: DefaultMatCalendarRangeStrategy,
    },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NimbusFilterDatetimeRangeInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: NimbusFilterDatetimeRangeInputComponent,
      multi: true,
    },
  ],
})
export class NimbusFilterDatetimeRangeInputComponent extends ControlValueAccessorConnector<DateTimeRangeValue> implements OnInit {
  @Input() public dateFormat: string = 'YYYY-MM-DD';
  @Input() public timeFormat: string = 'HH:mm:ss';
  @Input() public locale: string = 'en';
  @Input() public quickActions: DateRangeQuickAction[];
  @Input() public minDate: Date;
  @Input() public maxDate: Date;

  @ViewChild(MatCalendar) public calendar: MatCalendar<Date>;

  protected readonly TranslationKeys = TranslationKeys;

  public header = InputDatePickerHeaderComponent;

  public form: FormGroup;
  public selectedDateRange: DateRange<Date>;
  public selectedDateFormat$ = this.store.select(LocalizationState.selectedDateFormat);

  public constructor(
    private readonly formBuilder: FormBuilder,
    private readonly store: Store,
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form = this.formBuilder.group({
      start: [moment().startOf('day'), [dateBetweenValidator(this.minDate, this.maxDate)]],
      end: [moment().endOf('day'), [dateBetweenValidator(this.minDate, this.maxDate)]],
      startTime: [
        duration({
          hours: 0,
          minutes: 0,
          seconds: 0,
        }),
      ],
      endTime: [
        duration({
          hours: 23,
          minutes: 59,
          seconds: 59,
        }),
      ],
    });

    this.selectedDateRange = new DateRange<Date>(moment().startOf('day').toDate(), moment().toDate());
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.control.setValue(this.mapToValue());

      this.selectedDateRange = new DateRange<Date>(this.form.getRawValue()?.start?.toDate(), this.form.getRawValue()?.end?.toDate());
    });
  }

  public writeValue(val: DateTimeRangeValue): void {
    super.writeValue(val);
    this.mapToForm(val);
  }

  public onSelectedChanged(date: Date): void {
    if (this.selectedDateRange && this.selectedDateRange.start && date >= this.selectedDateRange.start && !this.selectedDateRange.end) {
      this.selectedDateRange = new DateRange(this.selectedDateRange.start, date);
      const end = moment(this.selectedDateRange.end).endOf('day');
      this.form.patchValue({
        start: moment(this.selectedDateRange.start),
        end: moment(end),
        startTime: this.form.value.startTime,
        endTime: duration({
          hours: end.hours(),
          minutes: end.minutes(),
          seconds: end.seconds(),
        }),
      });
    } else {
      this.selectedDateRange = new DateRange(date, null);
      this.form.setValue({
        start: moment(this.selectedDateRange.start),
        end: null,
        startTime: duration(this.form.value.startTime),
        endTime: null,
      });
    }
  }

  public onQuickActionClick(action: DateRangeQuickAction): void {
    this.writeValue(action.generator());
    this.calendar?._goToDateInView(moment(action.generator().end).toDate(), 'month');
  }

  public isQuickActionSelected(quickAction: DateRangeQuickAction): boolean {
    const { start, end } = quickAction.generator();
    const currentStart = this.form.controls.start.value;
    const currentEnd = this.form.controls.end.value;
    return currentEnd && currentStart && currentStart.isSame(moment(start)) && currentEnd.isSame(moment(end));
  }

  private mapToForm(val: DateTimeRangeValue): void {
    if (val) {
      const start = val.start ? moment(val.start) : null;
      const end = val.end ? moment(val.end) : null;
      this.form.setValue({
        start: start?.isValid() ? start : null,
        end: end?.isValid() ? end : null,
        startTime: start?.isValid()
          ? duration({
              hours: start.hours(),
              minutes: start.minutes(),
              seconds: start.seconds(),
            })
          : duration({
              hours: 0,
              minutes: 0,
              seconds: 0,
            }),
        endTime: end?.isValid()
          ? duration({
              hours: end.hours(),
              minutes: end.minutes(),
              seconds: end.seconds(),
            })
          : duration({
              hours: 23,
              minutes: 59,
              seconds: 59,
            }),
      });

      this.selectedDateRange = new DateRange<Date>(start?.toDate(), end?.toDate());
    }
  }

  private mapToValue(): DateTimeRangeValue {
    const val = this.form.value;
    const start = val.start ? moment(val.start, this.dateFormat).startOf('day').add(duration(val.startTime)) : null;
    const end = val.end ? moment(val.end, this.dateFormat).startOf('day').add(duration(val.endTime)) : null;
    return {
      start: start?.isValid() ? start.toISOString() : null,
      end: end?.isValid() ? end.toISOString() : null,
    };
  }
}
